/**
 * Query options shared by a workspace page's components and its route loader.
 *
 * Each page below renders its own skeletons and owns its filter state, so its
 * loader does not await anything: it only *starts* the request for the page's
 * first render — the default filters — as soon as the navigation (or an
 * intent preload on hover) begins. That only helps when the loader warms the
 * exact key the component then subscribes to, which is why both read the
 * options from here rather than repeating a key literal.
 *
 * Everything here is a plain read, so warming it early has no side effects.
 */

import { infiniteQueryOptions, type QueryClient, queryOptions } from "@tanstack/react-query";
import { listAlerts } from "@/core/lib/alerts-api";
import { fetchPropertiesPage, getProperty, type PropertiesPageParams } from "@/core/lib/asset-api";
import { getAssetIssue, listAssetIssueNotifications } from "@/core/lib/asset-issue-api";
import {
  fetchAttendanceUsage,
  fetchEmployeeHistory,
  fetchSession,
  fetchSessionsPage,
} from "@/core/lib/attendance-api";
import { listBroadcastLists } from "@/core/lib/broadcast-api";
import { fetchDocumentsPage } from "@/core/lib/documents-api";
import {
  type EmployeeListParams,
  fetchEmployeesPage,
  listEmployeeDirectory,
} from "@/core/lib/employee-api";
import {
  fetchIncidentsPage,
  getIncident,
  type IncidentsPageParams,
  listIncidentAttachments,
  listIncidentDocuments,
  listIncidentNotes,
} from "@/core/lib/incident-api";
import { fetchPoliciesPage } from "@/core/lib/policy-api";
import {
  fetchHrRequestCounts,
  fetchHrRequests,
  fetchHrRequestsPage,
  type RequestsPageParams,
} from "@/core/lib/requests-api";
import { fetchLeaveOverview } from "@/lib/leave-api";
import { alertCountsQuery } from "@/lib/loaders";
import { fetchOnboardingOverview } from "@/lib/onboarding-api";
import { type Area, atLeast, type Permissions } from "@/lib/permissions";

/** Page size for the "load more" lists below. */
const PAGE_SIZE = 25;

/** Offset-based "load more": the next offset is how many rows are loaded. */
function nextOffset<P extends { items: unknown[]; total: number }>(lastPage: P, allPages: P[]) {
  const loaded = allPages.reduce((n, p) => n + p.items.length, 0);
  return loaded < lastPage.total ? loaded : undefined;
}

/**
 * Starts a request without holding up the navigation. The page surfaces a
 * failure itself; this is only a head start.
 */
export function warm(request: Promise<unknown>): void {
  void request.catch(() => {});
}

/* ---------------------------------------------------------------------- *
 * Dashboard home
 * ---------------------------------------------------------------------- */

/** How many rows the dashboard's "recent" lists show. */
export const RECENT_LIMIT = 5;

export const dashboardEmployeesCountQuery = queryOptions({
  queryKey: ["dashboard", "employees-count"],
  queryFn: () => fetchEmployeesPage({ page: 1, pageSize: 1, sort: "name", dir: "asc" }),
});

export const dashboardRecentDocumentsQuery = queryOptions({
  queryKey: ["dashboard", "documents-recent"],
  queryFn: () =>
    fetchDocumentsPage({ limit: RECENT_LIMIT, offset: 0, sort: "updated", dir: "desc" }),
});

export const dashboardUpcomingAlertsQuery = queryOptions({
  queryKey: ["dashboard", "alerts-upcoming"],
  queryFn: () => listAlerts("upcoming", undefined, 10),
});

export const hrRequestCountsQuery = queryOptions({
  queryKey: ["requests", "counts"],
  queryFn: fetchHrRequestCounts,
});

export const dashboardRequestsQuery = queryOptions({
  queryKey: ["dashboard", "requests"],
  queryFn: fetchHrRequests,
});

/** Every dashboard widget's request, for the route loader. */
export function warmDashboard(queryClient: QueryClient, permissions: Permissions | null): void {
  // Each widget loads and fails independently, so each is warmed separately —
  // and only for what the role can see (`null`: a personal workspace).
  const can = (area: Area) => !permissions || atLeast(permissions[area], "view");
  if (can("employees")) warm(queryClient.ensureQueryData(dashboardEmployeesCountQuery));
  if (can("documents")) warm(queryClient.ensureQueryData(dashboardRecentDocumentsQuery));
  if (can("alerts")) {
    warm(queryClient.ensureQueryData(alertCountsQuery));
    warm(queryClient.ensureQueryData(dashboardUpcomingAlertsQuery));
  }
  if (can("requests")) {
    warm(queryClient.ensureQueryData(hrRequestCountsQuery));
    warm(queryClient.ensureQueryData(dashboardRequestsQuery));
  }
}

/* ---------------------------------------------------------------------- *
 * Lists
 * ---------------------------------------------------------------------- */

/** The employees table's default view: first page, 25 rows, sorted by name. */
export const DEFAULT_EMPLOYEE_LIST_PARAMS: EmployeeListParams = {
  page: 1,
  pageSize: 25,
  sort: "name",
  dir: "asc",
};

export const employeesPageQuery = (params: EmployeeListParams) =>
  queryOptions({
    queryKey: ["employees", params],
    queryFn: () => fetchEmployeesPage(params),
  });

export type AssetFilters = Pick<PropertiesPageParams, "q" | "category" | "sort" | "dir">;

export const assetsPageQuery = (filters: AssetFilters) =>
  infiniteQueryOptions({
    queryKey: ["assets", "page", filters],
    queryFn: ({ pageParam }) =>
      fetchPropertiesPage({ limit: PAGE_SIZE, offset: pageParam, ...filters }),
    initialPageParam: 0,
    getNextPageParam: nextOffset,
  });

export type IncidentFilters = Pick<
  IncidentsPageParams,
  "q" | "status" | "severity" | "type" | "sort" | "dir"
>;

export const incidentsPageQuery = (filters: IncidentFilters) =>
  infiniteQueryOptions({
    queryKey: ["incidents", "page", filters],
    queryFn: ({ pageParam }) =>
      fetchIncidentsPage({ limit: PAGE_SIZE, offset: pageParam, ...filters }),
    initialPageParam: 0,
    getNextPageParam: nextOffset,
  });

export type PolicyFilters = { q?: string; published?: boolean };

export const policiesPageQuery = (filters: PolicyFilters) =>
  infiniteQueryOptions({
    queryKey: ["policies", "page", filters],
    queryFn: ({ pageParam }) =>
      fetchPoliciesPage({ limit: PAGE_SIZE, offset: pageParam, ...filters }),
    initialPageParam: 0,
    getNextPageParam: nextOffset,
  });

export type HrRequestFilters = Pick<RequestsPageParams, "q" | "status" | "type">;

/** The inbox opens on the Pending tab. */
export const DEFAULT_HR_REQUEST_FILTERS: HrRequestFilters = { status: "pending" };

export const hrRequestsPageQuery = (filters: HrRequestFilters) =>
  infiniteQueryOptions({
    queryKey: ["hr-requests", "page", filters],
    queryFn: ({ pageParam }) =>
      fetchHrRequestsPage({ limit: PAGE_SIZE, offset: pageParam, ...filters }),
    initialPageParam: 0,
    getNextPageParam: nextOffset,
  });

/* ---------------------------------------------------------------------- *
 * Overviews
 * ---------------------------------------------------------------------- */

export const leaveOverviewQuery = queryOptions({
  queryKey: ["leave", "overview"],
  queryFn: fetchLeaveOverview,
});

export const onboardingOverviewQuery = queryOptions({
  queryKey: ["onboarding", "overview"],
  queryFn: fetchOnboardingOverview,
});

/** Employee totals for the broadcast/policy audience pickers. */
export const broadcastEmployeeMetaQuery = queryOptions({
  queryKey: ["employees", "broadcast-meta"],
  queryFn: () => fetchEmployeesPage({ page: 1, pageSize: 1, sort: "name", dir: "asc" }),
});

export const broadcastListsQuery = queryOptions({
  queryKey: ["broadcast-lists"],
  queryFn: listBroadcastLists,
});

export type AttendanceFilters = Omit<Parameters<typeof fetchSessionsPage>[0], "page">;

/** Attendance sessions, a page at a time (the API pages by number, not offset). */
export const attendanceSessionsQuery = (filters: AttendanceFilters) =>
  infiniteQueryOptions({
    queryKey: ["attendance", "sessions", filters],
    queryFn: ({ pageParam }) => fetchSessionsPage({ ...filters, page: pageParam }),
    initialPageParam: 1,
    getNextPageParam: (last) =>
      last.page * last.pageSize < last.total ? last.page + 1 : undefined,
  });

export const attendanceSessionQuery = (id: string) =>
  queryOptions({
    queryKey: ["attendance", "session", id],
    queryFn: () => fetchSession(id),
  });

export const attendanceUsageQuery = queryOptions({
  queryKey: ["attendance", "usage"],
  queryFn: fetchAttendanceUsage,
});

export const employeeAttendanceQuery = (employeeId: string) =>
  queryOptions({
    queryKey: ["attendance", "employee", employeeId],
    queryFn: () => fetchEmployeeHistory(employeeId),
  });

/* ---------------------------------------------------------------------- *
 * Detail pages
 * ---------------------------------------------------------------------- */

/** Every employee's name, for the pickers and labels on the detail pages. */
export const employeeDirectoryQuery = queryOptions({
  queryKey: ["employees"],
  queryFn: listEmployeeDirectory,
});

export const incidentQuery = (id: string) =>
  queryOptions({
    queryKey: ["incident", id],
    queryFn: () => getIncident(id),
  });

export const incidentNotesQuery = (id: string) =>
  queryOptions({
    queryKey: ["incident", id, "notes"],
    queryFn: () => listIncidentNotes(id),
  });

export const incidentAttachmentsQuery = (id: string) =>
  queryOptions({
    queryKey: ["incident", id, "attachments"],
    queryFn: () => listIncidentAttachments(id),
  });

export const incidentDocumentsQuery = (id: string) =>
  queryOptions({
    queryKey: ["incident", id, "documents"],
    queryFn: () => listIncidentDocuments(id),
  });

export const assetPropertyQuery = (id: string) =>
  queryOptions({
    queryKey: ["asset-property", id],
    queryFn: () => getProperty(id),
  });

export const assetIssueQuery = (id: string) =>
  queryOptions({
    queryKey: ["asset-issue", id],
    queryFn: () => getAssetIssue(id),
  });

export const assetIssueNotificationsQuery = queryOptions({
  queryKey: ["asset-issue-notifications"],
  queryFn: listAssetIssueNotifications,
});
